import { ISO_WEEK_RULES, type WeekRules } from './model';

// CLDR48 supplemental/weekData: fallback for engines without week info/minimalDays.
// https://github.com/unicode-org/cldr/blob/release-48/common/supplemental/supplementalData.xml#L4676
const fourDays = new Set('AD AN AT AX BE BG CH CZ DE DK EE ES FI FJ FO FR GB GF GG GI GP GR HU IE IM IS IT JE LI LT LU MC MQ NL NO PL PT RE RU SE SJ SK SM VA'.split(' '));
const sunday = new Set('AG AS BD BR BS BT BW BZ CA CO DM DO ET GT GU HK HN ID IL IN IS JM JP KE KH KR LA MH MM MO MT MX MZ NI NP PA PE PH PK PR PT PY SA SG SV TH TT TW UM US VE VI WS YE ZA ZW'.split(' '));
const saturday = new Set('AF BH DJ DZ EG IQ IR JO KW LY OM QA SD SY'.split(' '));

export interface WeekRuleOptions {
  numbering?: 'regional' | 'iso';
  /** Regionless UI languages can use the user's German timezone as a regional hint. Explicit locale regions win. */
  timeZone?: string;
  /** Browser regional locale, independent of the language used for UI text. */
  regionLocale?: string;
}

/** Locale/region controls the week convention; timezone never converts the caller's calendar fields. */
export function weekRulesFor(locale: string, { numbering = 'regional', timeZone, regionLocale }: WeekRuleOptions = {}): WeekRules {
  if (numbering === 'iso') return ISO_WEEK_RULES;
  try {
    let parsed = new Intl.Locale(locale);
    const regionOverride = /-rg-([a-z]{2})zzzz/i.exec(parsed.toString())?.[1]?.toUpperCase();
    if (!parsed.region && !regionOverride) {
      let region: string | undefined;
      if (timeZone === 'Europe/Berlin' || timeZone === 'Europe/Busingen') region = 'DE';
      else if (regionLocale) { try { region = new Intl.Locale(regionLocale).region; } catch { /* Keep the UI language's regional default. */ } }
      if (region) parsed = new Intl.Locale(parsed, { region });
    }
    const region = regionOverride ?? parsed.maximize().region ?? '001';
    const info = parsed as Intl.Locale & { getWeekInfo?: () => { firstDay: number; minimalDays?: number }; weekInfo?: { firstDay: number; minimalDays?: number } };
    const weekInfo = info.getWeekInfo?.() ?? info.weekInfo;
    const firstDay = weekInfo?.firstDay;
    return {
      weekStartsOn: firstDay === undefined ? region === 'MV' ? 5 : saturday.has(region) ? 6 : sunday.has(region) ? 0 : 1 : firstDay % 7,
      minimalDays: weekInfo?.minimalDays ?? (fourDays.has(region) ? 4 : 1),
    };
  } catch { return ISO_WEEK_RULES; }
}
